import { memo, useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Icon,
  Switch
} from '@clientbase/clientbase-library'
import { alpha, Box, Divider, Skeleton, Stack, Typography } from '@mui/material'
import { notificationService } from 'services/notifications'
import {
  NotificationPreferences,
  NotificationEventConfig,
  NOTIFICATION_EVENT_CONFIGS
} from 'services/notifications/types'

import { theme } from 'styles/theme'

const SOUND_STORAGE_KEY = 'notification_sound_enabled'

const REMINDER_DAYS_OPTIONS = [0, 1, 2, 3, 5, 7, 10, 15, 30]
const DEFAULT_REMINDER_DAYS = [5, 3, 0]

// Event types that support configurable reminder_days, keyed by eventKey
const REMINDER_DAYS_EVENTS: Record<string, { label: string }> = {
  accounts_payable_due_soon: {
    label: 'Lembrar-me com antecedência:'
  },
  contract_expiring: {
    label: 'Lembrar antes do vencimento do contrato:'
  },
  recurrence_ending: {
    label: 'Lembrar antes do fim da recorrência:'
  }
}

const getReminderDayLabel = (day: number): string => {
  if (day === 0) return 'No dia'
  if (day === 1) return '1 dia'
  return `${day} dias`
}

const CATEGORY_CONFIG: Record<
  string,
  { label: string; icon: string; description: string; color: string }
> = {
  billing: {
    label: 'Cobranças',
    icon: 'receipt',
    description: 'Pagamentos, vencimentos e recorrências',
    color: theme.palette.primary.main
  },
  contract: {
    label: 'Contratos',
    icon: 'description',
    description: 'Assinaturas e vencimentos de contratos',
    color: theme.palette.info[300]
  },
  customer: {
    label: 'Clientes',
    icon: 'person',
    description: 'Novos cadastros e atualizações',
    color: theme.palette.success[300]
  },
  nfse: {
    label: 'Notas Fiscais',
    icon: 'receipt',
    description: 'Emissão e erros de NFSe',
    color: theme.palette.secondary[100]
  },
  transfer: {
    label: 'Transferências',
    icon: 'accountBalance',
    description: 'Status de transferências bancárias',
    color: theme.palette.secondary[200]
  },
  accounts_payable: {
    label: 'Contas a Pagar',
    icon: 'paid',
    description: 'Vencimentos de contas a pagar',
    color: theme.palette.secondary[200]
  },
  system: {
    label: 'Sistema',
    icon: 'settings',
    description: 'Relatórios, importações e processamentos em lote',
    color: theme.palette.alert[300]
  }
}

interface NotificationSettingsProps {
  onClose?: () => void
  onHasChangesChange?: (hasChanges: boolean) => void
  onSaveCallbackChange?: (callback: (() => Promise<void>) | null) => void
}

const NotificationSettings = ({
  onClose,
  onHasChangesChange,
  onSaveCallbackChange
}: NotificationSettingsProps) => {
  const [preferences, setPreferences] = useState<NotificationPreferences>({})
  const [isLoading, setIsLoading] = useState(true)
  const [hasChanges, setHasChanges] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(() => {
    if (typeof window === 'undefined') return true
    return localStorage.getItem(SOUND_STORAGE_KEY) !== 'false'
  })

  const fetchPreferences = useCallback(async () => {
    setIsLoading(true)
    try {
      const prefs = await notificationService.getPreferences()
      setPreferences(prefs || {})
    } catch (error) {
      console.error('Error fetching preferences:', error)
      setPreferences({})
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPreferences()
  }, [fetchPreferences])

  useEffect(() => {
    onHasChangesChange?.(hasChanges)
  }, [hasChanges, onHasChangesChange])

  const handleSave = useCallback(async () => {
    try {
      await notificationService.updatePreferences(preferences)
      toast.success('Preferências salvas com sucesso')
      setHasChanges(false)
      onClose?.()
    } catch (error) {
      console.error('Error saving preferences:', error)
      toast.error('Erro ao salvar preferências')
      throw error
    }
  }, [preferences, onClose])

  useEffect(() => {
    onSaveCallbackChange?.(handleSave)
    return () => onSaveCallbackChange?.(null)
  }, [handleSave, onSaveCallbackChange])

  const handleToggleEvent = (eventKey: string, enabled: boolean) => {
    setPreferences((prev) => ({
      ...prev,
      [eventKey]: {
        ...((prev as Record<string, unknown>)[eventKey] as Record<
          string,
          unknown
        >),
        enabled
      }
    }))
    setHasChanges(true)
  }

  const handleToggleSound = (enabled: boolean) => {
    setSoundEnabled(enabled)
    localStorage.setItem(SOUND_STORAGE_KEY, String(enabled))
  }

  const isEventEnabled = (eventKey: string): boolean => {
    const eventPref = (preferences as Record<string, unknown>)[eventKey] as
      | { enabled?: boolean }
      | undefined
    return eventPref?.enabled !== false
  }

  const getReminderDays = (eventKey: string): number[] => {
    const eventPref = (preferences as Record<string, unknown>)[eventKey] as
      | { reminder_days?: number[] }
      | undefined
    return eventPref?.reminder_days ?? DEFAULT_REMINDER_DAYS
  }

  const handleToggleReminderDay = (eventKey: string, day: number) => {
    const current = getReminderDays(eventKey)
    const updated = current.includes(day)
      ? current.filter((d) => d !== day)
      : [...current, day]

    setPreferences((prev) => ({
      ...prev,
      [eventKey]: {
        ...((prev as Record<string, unknown>)[eventKey] as Record<
          string,
          unknown
        >),
        enabled:
          (
            (prev as Record<string, unknown>)[eventKey] as
              | { enabled?: boolean }
              | undefined
          )?.enabled !== false,
        reminder_days: updated
      }
    }))
    setHasChanges(true)
  }

  const groupedEvents = NOTIFICATION_EVENT_CONFIGS.reduce((acc, event) => {
    if (!acc[event.category]) {
      acc[event.category] = []
    }
    acc[event.category].push(event)
    return acc
  }, {} as Record<string, NotificationEventConfig[]>)

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" gap={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 2,
            borderRadius: '8px',
            border: `1px solid ${theme.palette.neutral[700]}`
          }}
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Skeleton
              variant="rounded"
              width={36}
              height={36}
              sx={{ borderRadius: '8px', flexShrink: 0 }}
            />
            <Stack gap={0.5}>
              <Skeleton variant="text" width={140} height={18} />
              <Skeleton variant="text" width={220} height={14} />
            </Stack>
          </Stack>
          <Skeleton
            variant="rounded"
            width={42}
            height={24}
            sx={{ borderRadius: '12px' }}
          />
        </Stack>

        <Divider sx={{ borderColor: theme.palette.neutral[700], my: 0.5 }} />

        {[1, 2, 3, 4].map((i) => (
          <Stack
            key={i}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: 2,
              borderRadius: '8px',
              border: `1px solid ${theme.palette.neutral[700]}`
            }}
          >
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Skeleton
                variant="rounded"
                width={36}
                height={36}
                sx={{ borderRadius: '8px', flexShrink: 0 }}
              />
              <Stack gap={0.5}>
                <Skeleton variant="text" width={100 + i * 20} height={18} />
                <Skeleton variant="text" width={180 + i * 10} height={14} />
              </Stack>
            </Stack>
            <Skeleton variant="text" width={25} height={16} />
          </Stack>
        ))}
      </Box>
    )
  }

  return (
    <Box display="flex" flexDirection="column" gap={1.5} pt={3}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          p: 2,
          borderRadius: '8px',
          border: `1px solid ${theme.palette.neutral[700]}`,
          backgroundColor: '#FAFBFA'
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Icon
              icon={soundEnabled ? 'faBell' : 'faBellSlash'}
              colorSX={theme.palette.primary.main}
              width="18px"
              height="18px"
            />
          </Box>
          <Stack gap={0.25}>
            <Typography
              variant="smBold"
              sx={{
                color: theme.palette.neutral[200],
                fontSize: '14px',
                fontWeight: 600
              }}
            >
              Som de notificação
            </Typography>
            <Typography
              variant="xsLight"
              sx={{
                color: theme.palette.neutral[400],
                fontSize: '12px'
              }}
            >
              Tocar som ao receber novas notificações
            </Typography>
          </Stack>
        </Stack>
        <Switch
          checked={soundEnabled}
          onChange={(e) => handleToggleSound(e.target.checked)}
        />
      </Stack>

      <Divider sx={{ borderColor: theme.palette.neutral[700], my: 0.5 }} />

      {Object.entries(groupedEvents).map(([category, events]) => {
        const config = CATEGORY_CONFIG[category] || {
          label: category,
          icon: 'faBell',
          description: '',
          color: theme.palette.primary.main
        }
        const enabledCount = events.filter((e) => isEventEnabled(e.key)).length

        return (
          <Accordion
            key={category}
            defaultExpanded={false}
            sx={{
              border: '1px solid #E8EBE8',
              borderLeft: '3px solid',
              borderLeftColor: config.color,
              borderRadius: '8px !important',
              backgroundColor: '#FAFBFA',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0,
                backgroundColor: '#fff'
              }
            }}
          >
            <AccordionSummary
              aria-controls={`${category}-content`}
              id={`${category}-header`}
              sx={{
                padding: '4px 16px',
                minHeight: '64px',
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0'
                }
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                pr={1}
              >
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      backgroundColor: alpha(config.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Icon
                      icon={config.icon as never}
                      colorSX={config.color}
                      width="18px"
                      height="18px"
                    />
                  </Box>
                  <Stack gap={0.25}>
                    <Typography
                      variant="smBold"
                      sx={{
                        color: theme.palette.neutral[200],
                        fontSize: '14px',
                        fontWeight: 600
                      }}
                    >
                      {config.label}
                    </Typography>
                    <Typography
                      variant="xsLight"
                      sx={{
                        color: theme.palette.neutral[400],
                        fontSize: '12px'
                      }}
                    >
                      {config.description}
                    </Typography>
                  </Stack>
                </Stack>
                <Typography
                  variant="xsBold"
                  sx={{
                    color: theme.palette.neutral[400],
                    fontSize: '11px',
                    fontWeight: 600
                  }}
                >
                  {enabledCount}/{events.length}
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ padding: '12px 16px 16px' }}>
              <Stack
                gap={0}
                sx={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: `1px solid ${theme.palette.neutral[700]}`
                }}
              >
                {events.map((event, index) => {
                  const reminderConfig = REMINDER_DAYS_EVENTS[event.key]

                  return (
                    <Stack
                      key={event.key}
                      sx={{
                        backgroundColor:
                          index % 2 === 0
                            ? 'transparent'
                            : alpha(theme.palette.neutral[800], 0.5),
                        borderBottom:
                          index < events.length - 1
                            ? `1px solid ${theme.palette.neutral[700]}`
                            : 'none'
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ py: 1.5, px: 2 }}
                      >
                        <Stack gap={0.25} flex={1} pr={2}>
                          <Typography
                            variant="smLight"
                            sx={{
                              color: theme.palette.neutral[200],
                              fontSize: '13px',
                              fontWeight: 500
                            }}
                          >
                            {event.label}
                          </Typography>
                          <Typography
                            variant="xsLight"
                            sx={{
                              color: theme.palette.neutral[450],
                              fontSize: '11px'
                            }}
                          >
                            {event.description}
                          </Typography>
                        </Stack>

                        <Switch
                          checked={isEventEnabled(event.key)}
                          onChange={(e) =>
                            handleToggleEvent(event.key, e.target.checked)
                          }
                        />
                      </Stack>

                      {reminderConfig && (
                        <Box sx={{ px: 2, pb: 1.5 }}>
                          <Typography
                            variant="xsBold"
                            sx={{
                              color: theme.palette.neutral[300],
                              fontSize: '12px',
                              fontWeight: 600,
                              mb: 1
                            }}
                          >
                            {reminderConfig.label}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '6px'
                            }}
                          >
                            {REMINDER_DAYS_OPTIONS.map((day) => {
                              const isSelected = getReminderDays(
                                event.key
                              ).includes(day)
                              const isDisabled = !isEventEnabled(event.key)

                              return (
                                <Box
                                  key={day}
                                  component="button"
                                  onClick={() =>
                                    !isDisabled &&
                                    handleToggleReminderDay(event.key, day)
                                  }
                                  sx={{
                                    all: 'unset',
                                    cursor: isDisabled ? 'default' : 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '4px 10px',
                                    borderRadius: '16px',
                                    fontSize: '12px',
                                    fontWeight: isSelected ? 600 : 400,
                                    fontFamily: 'Inter, sans-serif',
                                    whiteSpace: 'nowrap',
                                    transition: 'all 150ms linear',
                                    opacity: isDisabled ? 0.4 : 1,
                                    color: isSelected
                                      ? theme.palette.primary[100]
                                      : theme.palette.neutral[400],
                                    backgroundColor: isSelected
                                      ? `${theme.palette.primary[100]}14`
                                      : 'transparent',
                                    border: `1px solid ${
                                      isSelected
                                        ? `${theme.palette.primary[100]}40`
                                        : theme.palette.neutral[700]
                                    }`,
                                    ...(!isDisabled && {
                                      '&:hover': {
                                        borderColor: isSelected
                                          ? `${theme.palette.primary[100]}60`
                                          : theme.palette.neutral[500],
                                        backgroundColor: isSelected
                                          ? `${theme.palette.primary[100]}1F`
                                          : `${theme.palette.neutral[700]}50`
                                      }
                                    })
                                  }}
                                >
                                  {getReminderDayLabel(day)}
                                </Box>
                              )
                            })}
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  )
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )
      })}
    </Box>
  )
}

export default memo(NotificationSettings)
